import { z } from 'zod';

export const nameFieldMappingSchema = z.union([
  z.strictObject({ fullName: z.string() }),
  z.strictObject({ firstName: z.string(), lastName: z.string() }),
]);
export const agencyZoomFieldMappingSchema = z
  .strictObject({
    name: nameFieldMappingSchema,
    businessname: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    otherEmail: z.string().optional(),
    otherPhone: z.string().optional(),
    notes: z.string().optional(),
    birthday: z.string().describe('Source field containing a YYYY-MM-DD date.').optional(),
    streetAddress: z.string().optional(),
    streetAddressLine2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    country: z.string().optional(),
    zip: z.string().optional(),
    currentPremium: z.string().optional(),
    maritalStatus: z.string().optional(),
    propertyZip: z.string().optional(),
    propertyCity: z.string().optional(),
    propertyState: z.string().optional(),
    propertyCountry: z.string().optional(),
    propertyAddress1: z.string().optional(),
    propertyAddress2: z.string().optional(),
  })
  .describe('All submitted fields are included in generated notes, appended to any mapped notes.');
export const agencyMvpFieldMappingSchema = z
  .strictObject({
    name: nameFieldMappingSchema,
    street1: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postal_code: z.string().optional(),
    preferred_callback: z.string().optional(),
    gender: z.string().optional(),
    birth_date: z.string().optional(),
    is_married: z.string().optional(),
    email: z.string().optional(),
    phone_number: z.string().optional(),
    mobile_number: z.string().optional(),
    has_sms_consent: z.string().optional(),
    occupation: z.string().optional(),
    household_relationship: z.string().optional(),
    height: z.string().optional(),
    weight: z.string().optional(),
    is_tobacco_user: z.string().optional(),
    has_pre_existing_conditions: z.string().optional(),
    has_prescriptions: z.string().optional(),
    has_military_service: z.string().optional(),
    credit_rating: z.string().optional(),
    has_dui: z.string().optional(),
    tags: z.string().optional(),
  })
  .describe(
    'All submitted fields are included in generated lead notes. The API token is configured on the Wirechunk worker.',
  );
export const highLevelFieldMappingSchema = z
  .strictObject({
    name: nameFieldMappingSchema,
    email: z.string().optional(),
    phone: z.string(),
    dateOfBirth: z.string().describe('Source field containing a YYYY-MM-DD date.').optional(),
    address1: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().describe('Source field containing a two-letter country code.').optional(),
  })
  .describe('All submitted fields are included in a separate contact note.');
export const ricochetFieldMappingSchema = z
  .strictObject({
    name: nameFieldMappingSchema,
    phone: z.string(),
  })
  .describe(
    'Every unmapped submitted field is forwarded unchanged under its original form field name. Do not add mappings for email, ZIP, or other fields; Ricochet maps those incoming fields after receiving a test lead. Wirechunk also adds Lead_Origination_Date and Wirechunk_Form_Entry_ID.',
  );
export const integrationMappingSchemas = {
  AgencyZoom: agencyZoomFieldMappingSchema,
  AgencyMvp: agencyMvpFieldMappingSchema,
  HighLevel: highLevelFieldMappingSchema,
  Ricochet360: ricochetFieldMappingSchema,
};
export const integrationKindSchema = z.enum([
  'Webhook',
  'AgencyZoom',
  'AgencyMvp',
  'HighLevel',
  'Ricochet360',
]);
export type IntegrationKind = z.infer<typeof integrationKindSchema>;

export const webhookConfigSchema = z
  .strictObject({
    webhookUrl: z.url(),
  })
  .describe('Webhooks POST every submitted field unchanged inside data, together with submission metadata.');
export const agencyZoomConfigSchema = z.strictObject({
  webhookUrl: z.url(),
  fieldMapping: agencyZoomFieldMappingSchema,
});
export const agencyMvpConfigSchema = z.strictObject({
  organizationId: z.string().trim().max(200).optional().describe('Required before publication and delivery.'),
  fieldMapping: agencyMvpFieldMappingSchema,
});
export const highLevelConfigSchema = z.strictObject({
  token: z
    .string()
    .trim()
    .max(8192)
    .regex(/^[^\r\n]+$/)
    .optional()
    .describe('Required before publication and delivery.'),
  locationId: z.string().max(200).optional().describe('Required before publication and delivery.'),
  fieldMapping: highLevelFieldMappingSchema,
});
export const ricochetConfigSchema = z.strictObject({
  webhookUrl: z.url(),
  fieldMapping: ricochetFieldMappingSchema,
});

export const integrationConfigSchema = z.discriminatedUnion('kind', [
  webhookConfigSchema.extend({ kind: z.literal('Webhook') }),
  agencyZoomConfigSchema.extend({ kind: z.literal('AgencyZoom') }),
  agencyMvpConfigSchema.extend({
    kind: z.literal('AgencyMvp'),
    organizationId: agencyMvpConfigSchema.shape.organizationId.unwrap().trim().min(1),
  }),
  highLevelConfigSchema.extend({
    kind: z.literal('HighLevel'),
    token: highLevelConfigSchema.shape.token.unwrap(),
    locationId: highLevelConfigSchema.shape.locationId.unwrap().trim().min(1),
  }),
  ricochetConfigSchema.extend({ kind: z.literal('Ricochet360') }),
]);
export type IntegrationConfig = z.infer<typeof integrationConfigSchema>;
